<script setup>
// Okno potwierdzenia usunięcia szablonu (plan usuwania szablonów, Etap 2).
// Montowane RAZ w App.vue, sterowane obietnicą z `useDestructiveConfirm` —
// ten sam wzorzec co `ConfirmDialog`, żeby w apce nie żyły dwa mechanizmy
// potwierdzeń o różnym zachowaniu.
import { ref, computed, watch, nextTick } from 'vue';
import { AlertTriangle, Unlink, ShieldAlert } from '@lucide/vue';
import Button from '@/components/ui/Button.vue';
import Input from '@/components/ui/Input.vue';
import { useDestructiveConfirmDialog } from '@/composables/useDestructiveConfirm';

const state = useDestructiveConfirmDialog();

const wpisanyToken = ref('');
const poleTokenu   = ref(null);

// Skutki z serwera. Ikona i kolor niosą różnicę, której samo zdanie nie odda:
// „zniknie" to utrata danych, „straci_link" to zerwane powiązanie.
const SKUTKI = {
    zniknie:     { ikona: AlertTriangle, opis: 'zostanie usunięte bezpowrotnie', klasa: 'text-destructive' },
    straci_link: { ikona: Unlink,        opis: 'straci powiązanie, ale zostanie', klasa: 'text-muted-foreground' },
    blokuje:     { ikona: ShieldAlert,   opis: 'blokuje usunięcie',               klasa: 'text-muted-foreground' },
};

const cokolwiekZniknie = computed(() =>
    (state.value?.zaleznosci ?? []).some(z => z.skutek === 'zniknie'));

// Dezaktywacja jest odwracalna, więc okno mówi o niej innym tonem i bez tokenu.
const toDezaktywacja = computed(() => state.value?.tryb === 'dezaktywacja');

const tokenPasuje = computed(() =>
    !state.value?.wymaga_tokenu
    || wpisanyToken.value.trim().toUpperCase() === (state.value?.token ?? '').toUpperCase());

// Ognisko na polu tokenu: to jedyna czynność, którą trzeba wykonać, żeby ruszyć
// dalej. Bez tokenu ogniskujemy przycisk anulowania — bezpieczne domyślne.
watch(state, async (nowy) => {
    wpisanyToken.value = '';
    if (!nowy) return;
    await nextTick();
    if (nowy.wymaga_tokenu) poleTokenu.value?.$el?.focus?.() ?? poleTokenu.value?.focus?.();
});

function odpowiedz(wynik) {
    if (!state.value) return;
    state.value.resolve(wynik);
    state.value = null;
}
</script>

<template>
    <Teleport to="body">
        <div
            v-if="state"
            class="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm"
            role="dialog"
            aria-modal="true"
            aria-labelledby="usuwanie-tytul"
            @click.self="odpowiedz(null)"
            @keydown.esc="odpowiedz(null)"
        >
            <div class="w-full max-w-md space-y-4 rounded-xl bg-popover p-4 text-popover-foreground ring-1 ring-foreground/10">
                <div>
                    <h3 id="usuwanie-tytul" class="text-base font-medium">
                        {{ toDezaktywacja ? `Wyłączyć „${state.nazwa}"?` : `Usunąć „${state.nazwa}"?` }}
                    </h3>
                    <p class="mt-2 text-sm text-muted-foreground">
                        <template v-if="toDezaktywacja">{{ state.coPrzestanieDzialac }}</template>
                        <template v-else>{{ cokolwiekZniknie
                            ? 'Ten szablon jest w użyciu. Usunięcie skasuje także poniższe dane.'
                            : 'Tej operacji nie da się cofnąć.' }}</template>
                    </p>
                </div>

                <!-- Zależności: liczby z serwera, nie ogólniki -->
                <ul v-if="state.zaleznosci?.length" class="space-y-2 rounded-lg border p-3">
                    <li v-for="z in state.zaleznosci" :key="z.etykieta" class="flex items-start gap-2 text-sm">
                        <component
                            :is="toDezaktywacja ? SKUTKI.straci_link.ikona : SKUTKI[z.skutek].ikona"
                            class="mt-0.5 h-4 w-4 shrink-0"
                            :class="toDezaktywacja ? 'text-muted-foreground' : SKUTKI[z.skutek].klasa"
                        />
                        <span>
                            <span class="font-medium tabular-nums">{{ z.liczba }}</span>
                            {{ z.etykieta }}<template v-if="!toDezaktywacja"> —
                            <span :class="SKUTKI[z.skutek].klasa">{{ SKUTKI[z.skutek].opis }}</span></template>
                        </span>
                    </li>
                </ul>

                <!-- Zdanie, dla którego to okno w ogóle powstało. -->
                <p v-if="toDezaktywacja" class="rounded-lg border border-dashed p-3 text-sm">
                    Dane już utworzone z tego szablonu <strong>zostają nietknięte</strong>.
                    Wyłączenie możesz cofnąć w każdej chwili.
                </p>

                <p v-if="state.bladWplywu" class="rounded-lg border border-dashed p-3 text-sm text-muted-foreground">
                    Nie udało się sprawdzić, co jest powiązane z tym szablonem. Usuwaj tylko wtedy, gdy masz pewność.
                </p>

                <!-- Token wymagany wyłącznie przy realnych zależnościach -->
                <div v-if="state.wymaga_tokenu" class="space-y-2">
                    <p class="text-sm">
                        Aby potwierdzić, przepisz kod:
                        <span class="ml-1 rounded bg-muted px-2 py-0.5 font-mono font-semibold tracking-widest select-all">{{ state.token }}</span>
                    </p>
                    <Input
                        ref="poleTokenu"
                        v-model="wpisanyToken"
                        class="font-mono tracking-widest uppercase"
                        placeholder="Przepisz kod"
                        aria-label="Kod potwierdzenia"
                        @keyup.enter="tokenPasuje && odpowiedz({ akcja: 'usun', token: state.token })"
                    />
                </div>

                <div v-if="toDezaktywacja" class="flex items-center gap-2">
                    <Button size="sm" @click="odpowiedz({ akcja: 'dezaktywuj' })">Wyłącz szablon</Button>
                    <Button size="sm" variant="ghost" class="ml-auto" @click="odpowiedz(null)">Anuluj</Button>
                </div>

                <div v-else class="flex flex-wrap items-center gap-2">
                    <!-- Droga bezpieczna jako akcja główna: dezaktywacja jest
                         odwracalna, a dane utworzone z szablonu zostają. -->
                    <Button
                        v-if="state.mozeDezaktywowac"
                        size="sm"
                        @click="odpowiedz({ akcja: 'dezaktywuj' })"
                    >
                        Dezaktywuj zamiast usuwać
                    </Button>
                    <!-- Wariant `destructive` zamiast `default` z czerwonym tekstem:
                         czerwień na niebieskim tle prymarnym dawała nieczytelny
                         kontrast. Gdy obok stoi droga bezpieczna, akcja groźna
                         schodzi do `outline`, żeby nie konkurowała wagą. -->
                    <Button
                        size="sm"
                        :variant="state.mozeDezaktywowac ? 'outline' : 'destructive'"
                        :class="state.mozeDezaktywowac && 'text-destructive hover:text-destructive'"
                        :disabled="!tokenPasuje"
                        @click="odpowiedz({ akcja: 'usun', token: state.token })"
                    >
                        Usuń trwale
                    </Button>
                    <Button size="sm" variant="ghost" class="ml-auto" @click="odpowiedz(null)">Anuluj</Button>
                </div>
            </div>
        </div>
    </Teleport>
</template>
